import { useEffect, useMemo } from 'react'

import { useForm, useNumericParams, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useCampaign, { useRelations } from '@hooks/useCampaign'
import useDataStoreItem from '@hooks/useDataStoreItem'
import useDataStoreItems from '@hooks/useDataStoreItems'

import portalDataSchema, {
  FieldSchema, findDataStoreItem, findDefaultValue, validateConditions,
} from '@functions/portalData'

import { requiredFields } from '@models/dataStoreItem'

const watchEntityKeys = [
  'campaignCategories',
  'campaignCommercialCategories',
  'campaignRuralCategories',
  'campaignSaleMethods',
  'campaignStatuses',
  'campaignTypes',
  'campaigns',
  'dataStoreItems',
]

const usePortalDataInput = (key: string) => {
  const {
    updatedEntities: {
      campaignCategories: campaignCategoriesUpdatedAt,
      campaignCommercialCategories: campaignCommercialCategoriesUpdatedAt,
      campaignRuralCategories: campaignRuralCategoriesUpdatedAt,
      campaignSaleMethods: campaignSaleMethodsUpdatedAt,
      campaignStatuses: campaignStatusesUpdatedAt,
      campaignTypes: campaignTypesUpdatedAt,
      campaigns: campaignsUpdatedAt,
      dataStoreItems: dataStoreItemsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignId } = useNumericParams()

  const { campaign } = useCampaign({ id: campaignId })
  const { filteredDataStoreItems } = useDataStoreItems(
    {
      filters: { owner_id: campaignId, owner_type: 'Campaign' },
    },
  )

  const campaignRelations = useRelations(campaign)

  const fieldSchema = portalDataSchema.fields.find((field => field.key === key)) as FieldSchema || {}

  const {
    campaignKey, conditions, label,
  } = fieldSchema

  const conditionsValid = useMemo(() => {
    const valid = validateConditions({
      conditions, campaign, campaignRelations, dataStoreItems: filteredDataStoreItems,
    })
    return valid
  }, [
    key,
    campaign.id,
    campaignsUpdatedAt,
    campaignCategoriesUpdatedAt,
    campaignCommercialCategoriesUpdatedAt,
    campaignSaleMethodsUpdatedAt,
    campaignRuralCategoriesUpdatedAt,
    campaignStatusesUpdatedAt,
    campaignTypesUpdatedAt,
    dataStoreItemsUpdatedAt,
  ])

  const dataStoreItem = useMemo(() => findDataStoreItem(
    { dataStoreItems: filteredDataStoreItems, key },
  ), [dataStoreItemsUpdatedAt, key])

  const defaultValue = findDefaultValue({ campaign, campaignRelations, fieldSchema })

  const defaultState = useMemo(
    () => ({
      data: {
        hidden: false,
        portal_data: true,
      },
      key,
      label: `Portal Data/${label}`,
      owner_id: campaign.id,
      owner_type: 'Campaign',
      value: '',
    }),
    [campaign.id, key],
  )

  const dataStoreItemFormPayload = useForm(
    defaultState,
    { entity: dataStoreItem, requiredFields },
    // @ts-expect-error TS2339
    [campaign.id, dataStoreItem.id, key],
  )

  const { callbacks: { createOrUpdateDataStoreItem } } = useDataStoreItem(dataStoreItem)
  const {
    blurFields, entityState, saveEnabled,
  } = dataStoreItemFormPayload

  const behaviour = 'replace'

  // Save the changes
  useEffect(() => {
    if (!!blurFields.length && saveEnabled){
      createOrUpdateDataStoreItem(behaviour, entityState)
    }
  }, [saveEnabled, JSON.stringify(entityState), blurFields.length])

  return {
    campaignKey,
    conditionsValid,
    dataStoreItem,
    dataStoreItemFormPayload,
    defaultValue,
    fieldSchema,
  }
}

export default usePortalDataInput
